import type { ReactNode } from 'react'
import { cn } from '@/components/ui'
import { Titre } from './Titre'

/**
 * Le chiffre clé — DA 2027 (lot 3, 2026-10-27).
 *
 * Un chiffre en Outfit 900 serré et son libellé en clair, sur aplat blanc :
 * « 6 · Séances réalisées », « 900 € · Reste à régler ». Le libellé n'est plus
 * en capitales espacées : la DA 2027 écrit normalement, majuscule en début de
 * phrase.
 *
 * - `neutre` : aplat blanc — le cas courant ;
 * - `accent` : aplat lavande, pour le chiffre qu'on veut faire voir ;
 * - `sourd` : texte adouci, pour un chiffre secondaire.
 *
 * ⚠️ La lavande pâle de la V4 (`lavender-soft`) est hors charte en 2027 : elle
 * n'est plus employée.
 */
export function Chiffre({
  valeur,
  libelle,
  ton = 'neutre',
}: {
  valeur: ReactNode
  libelle: string
  ton?: 'neutre' | 'accent' | 'sourd'
}) {
  return (
    <div
      className={cn(
        'flex flex-col justify-center rounded-[24px] px-5 py-[18px]',
        ton === 'accent' ? 'bg-lavender' : 'bg-surface',
      )}
    >
      <p
        className={cn(
          'text-[30px] leading-none font-black tracking-[-0.05em] oasis:text-[34px]',
          ton === 'sourd' ? 'text-ink-soft' : 'text-ink',
        )}
      >
        {valeur}
      </p>
      <p className="mt-2 text-[13.5px] leading-[1.3] text-ink-soft">{libelle}</p>
    </div>
  )
}

/**
 * Le titre de section — DA 2027 (lot 3, 2026-10-27).
 *
 * ⚠️ **Toujours AU-DESSUS de son contenu**, jamais à côté, et **à la même
 * taille partout** (`--t-section`) : deux décisions de Baptiste du 2026-10-27.
 * `accent` porte le mot en Playfair italique, point final compris :
 *
 *     <TitreSection titre="Quoi de" accent="neuf." intro="…" action={…} />
 *
 * Sans `accent`, le titre s'affiche tout en Outfit — c'est le rendu des écrans
 * qui ne sont pas encore passés au lot 4.
 */
export function TitreSection({
  titre,
  accent,
  intro,
  action,
}: {
  titre: string
  accent?: string
  intro?: string
  action?: ReactNode
}) {
  return (
    <div className="mt-16 mb-6 flex flex-wrap items-end justify-between gap-4 oasis:mt-[clamp(64px,6vw,128px)]">
      <div className="min-w-0">
        <Titre as="h2" accent={accent} className="text-(length:--t-section)">
          {titre}
        </Titre>
        {intro && (
          <p className="mt-3 max-w-[36em] text-[16px] text-ink-soft oasis:text-[clamp(16px,1.05vw,20px)]">
            {intro}
          </p>
        )}
      </div>
      {action}
    </div>
  )
}
